import React, { FC, ReactElement, useEffect } from 'react';

import styles from './styles.module.scss';

import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { DELEGATES } from '../../../../../../constants/organizationTabs';
import { PremiumTab } from '../../../../../PremiumTab';
import { OrganizationActions, OrganizationTabsActions } from 'store/actions';
import { ProposalsVotesGraph } from './components/ProposalsVotesGraph';
import { VotingActivity } from './components/VotingActivity';
import { organizationTrendsTableHeaders } from './constants/organization-trends.constants';
import { TrendsTable } from './components/TrendsTable';
import { YearByYearTable } from './components/YearByYearTable';
import { TRENDS_TAB, TRENDS_TAB_DESCRIPTIONS } from '../../../../../../constants';
import { OrganizationVoterTrendsProps } from './types';

export const OrganizationVoterTrends: FC<OrganizationVoterTrendsProps> = ({ isPremiumAllowed }): ReactElement => {
    const {
        organizationTabs,
        oviYearByYear,
        organizationProposalsTrends,
        organizationSuccessfulProposalsTrends,
        organizationVotesTrends,
        organizationVotersTrends,
        organizationAverageVotesPerProposalTrends,
    } = useAppSelector(({ organizationTabs }) => organizationTabs);
    const isTabBlocked = organizationTabs.data.find((t) => t.tab.name === DELEGATES)?.isBlocked;
    const { organizationData } = useAppSelector(({ organization }) => organization);
    const organizationId = organizationData.id;

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(OrganizationActions.getOrganizationActivityData.request({ organizationId }));
        if (isPremiumAllowed) {
            dispatch(OrganizationTabsActions.getOrganizationYearByYearVotes.request({ organizationId }));
            dispatch(OrganizationTabsActions.getOrganizationProposalsTrends.request({ organizationId }));
            dispatch(OrganizationTabsActions.getOrganizationSuccessfulProposalsTrends.request({ organizationId }));
            dispatch(OrganizationTabsActions.getOrganizationVotesTrends.request({ organizationId }));
            dispatch(OrganizationTabsActions.getOrganizationVotersTrends.request({ organizationId }));
            dispatch(OrganizationTabsActions.getOrganizationAverageVotesPerProposalTrends.request({ organizationId }));
        }
    }, [organizationId, isPremiumAllowed]);

    return (
        <div className={styles.container}>
            {isTabBlocked ? (
                <div className={styles.premiumWindow}>
                    <PremiumTab title="Voter Trends is a DeepDAO premium tab for this organization" />
                </div>
            ) : (
                <div className={styles.content}>
                    <div className={styles.mainInfoContent}>
                        <ProposalsVotesGraph />
                        <div className={styles.voterActivity}>
                            <VotingActivity showBlur={!isPremiumAllowed} />
                        </div>
                        <div className={styles.yearByYearTable}>
                            <YearByYearTable
                                title="Year by Year Voters"
                                infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.YEAR_BY_YEAR]}
                                lastUpdate={oviYearByYear.lastUpdateDate}
                                tableRowsData={oviYearByYear.data}
                                loading={oviYearByYear.loading}
                                showBlur={!isPremiumAllowed}
                            />
                        </div>
                    </div>
                    <div className={styles.trendsTablesWrapper}>
                        <TrendsTable
                            title="Proposals"
                            infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                            lastUpdate={organizationProposalsTrends.lastUpdate}
                            tableRowsData={organizationProposalsTrends.data}
                            loading={organizationProposalsTrends.loading}
                            tableHeaderData={organizationTrendsTableHeaders}
                            showBlur={!isPremiumAllowed}
                            blurTitle="Proposals is a DeepDAO Pro feature"
                            blurSubtitle="Upgrade to Pro to access detailed voter trends data"
                        />

                        <TrendsTable
                            title="Successful Proposals"
                            infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                            lastUpdate={organizationSuccessfulProposalsTrends.lastUpdate}
                            tableRowsData={organizationSuccessfulProposalsTrends.data}
                            loading={organizationSuccessfulProposalsTrends.loading}
                            tableHeaderData={organizationTrendsTableHeaders}
                            showBlur={!isPremiumAllowed}
                            blurTitle="Successful Proposals is a DeepDAO Pro feature"
                            blurSubtitle={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                        />

                        <TrendsTable
                            title="Votes"
                            infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                            lastUpdate={organizationVotesTrends.lastUpdate}
                            tableRowsData={organizationVotesTrends.data}
                            loading={organizationVotesTrends.loading}
                            tableHeaderData={organizationTrendsTableHeaders}
                            showBlur={!isPremiumAllowed}
                            blurTitle="Votes is a DeepDAO Pro feature"
                            blurSubtitle={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                        />

                        <TrendsTable
                            title="Voters"
                            infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                            lastUpdate={organizationVotersTrends.lastUpdate}
                            tableRowsData={organizationVotersTrends.data}
                            loading={organizationVotersTrends.loading}
                            tableHeaderData={organizationTrendsTableHeaders}
                            showBlur={!isPremiumAllowed}
                            blurTitle="Voters is a DeepDAO Pro feature"
                            blurSubtitle={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                        />

                        <TrendsTable
                            title="Average Votes per Proposal"
                            infoText={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                            lastUpdate={organizationAverageVotesPerProposalTrends.lastUpdate}
                            tableRowsData={organizationAverageVotesPerProposalTrends.data}
                            loading={organizationAverageVotesPerProposalTrends.loading}
                            tableHeaderData={organizationTrendsTableHeaders}
                            showBlur={!isPremiumAllowed}
                            blurTitle="Average Votes per Proposal is a DeepDAO Pro feature"
                            blurSubtitle={TRENDS_TAB_DESCRIPTIONS[TRENDS_TAB.OTHER_TABLES]}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
